import { useEffect, useState, useCallback } from "react";
import { ExplorerAPI } from "../../../../util/Requests";
import { ExplorerEnvironment } from "../../../../data/Cache";
import { NBAGameListItem } from "../../../../util/types";
import { QUARTERS, MINUTES, PLAYER_SLOTS, PORTLAND_TEAM_ID } from "./constants";
import { getCellKey } from "./QuarterGrid/utils";

export function useRotationPlan(gameId: string) {
  const [slotAssignments, setSlotAssignments] = useState<
    Record<string, number | null>
  >({});
  const [excludedPlayerIds, setExcludedPlayerIds] = useState<Set<number>>(
    new Set()
  );
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadRotationPlan = useCallback((id: string) => {
    setLoadingPlan(true);
    const api = new ExplorerAPI(ExplorerEnvironment.NBA);
    api
      .getRotationPlan(id)
      .then((plan) => {
        setSlotAssignments(plan?.slotAssignments ?? {});
        setExcludedPlayerIds(new Set(plan?.excludedPlayerIds ?? []));
        setHasUnsavedChanges(false);
      })
      .catch((error) => {
        console.error("Failed to load rotation plan:", error);
        setSlotAssignments({});
        setExcludedPlayerIds(new Set());
        setHasUnsavedChanges(false);
      })
      .finally(() => {
        setLoadingPlan(false);
      });
  }, []);

  useEffect(() => {
    if (!gameId) return;
    loadRotationPlan(gameId);
  }, [gameId, loadRotationPlan]);

  const assignPlayer = (
    quarter: string,
    minute: number,
    slot: number,
    playerId: number
  ) => {
    const minuteIndex = MINUTES.indexOf(minute);
    setHasUnsavedChanges(true);
    setSlotAssignments((prev) => {
      const next = { ...prev };
      for (let m = minuteIndex; m < MINUTES.length; m++) {
        next[getCellKey(quarter, MINUTES[m], slot)] = playerId;
      }
      return next;
    });
  };

  const clearSlot = (quarter: string, minute: number, slot: number) => {
    const minuteIndex = MINUTES.indexOf(minute);
    setHasUnsavedChanges(true);
    setSlotAssignments((prev) => {
      const next = { ...prev };
      for (let m = minuteIndex; m < MINUTES.length; m++) {
        next[getCellKey(quarter, MINUTES[m], slot)] = null;
      }
      return next;
    });
  };

  // Copies the lineup from the end of the previous quarter into all minutes of this quarter
  const carryOver = (quarter: string) => {
    setHasUnsavedChanges(true);
    const quarterIndex = QUARTERS.indexOf(quarter);
    const prevQuarter = QUARTERS[quarterIndex - 1];
    const lastMinute = MINUTES[MINUTES.length - 1];

    setSlotAssignments((prev) => {
      const next = { ...prev };
      PLAYER_SLOTS.forEach((slot) => {
        const playerId = prev[getCellKey(prevQuarter, lastMinute, slot)];
        MINUTES.forEach((minute) => {
          next[getCellKey(quarter, minute, slot)] = playerId;
        });
      });
      return next;
    });
  };

  const clearQuarter = (quarter: string) => {
    setHasUnsavedChanges(true);
    setSlotAssignments((prev) => {
      const next = { ...prev };
      PLAYER_SLOTS.forEach((slot) => {
        MINUTES.forEach((minute) => {
          next[getCellKey(quarter, minute, slot)] = null;
        });
      });
      return next;
    });
  };

  const excludePlayer = (playerId: number) => {
    setHasUnsavedChanges(true);
    setExcludedPlayerIds((prev) => new Set([...Array.from(prev), playerId]));
    setSlotAssignments((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key] === playerId) {
          next[key] = null;
        }
      });
      return next;
    });
  };

  const includePlayer = (playerId: number) => {
    setHasUnsavedChanges(true);
    setExcludedPlayerIds((prev) => {
      const next = new Set(Array.from(prev));
      next.delete(playerId);
      return next;
    });
  };

  const save = (selectedGame: NBAGameListItem) => {
    const opponentTeamId =
      selectedGame.homeTeam.teamId === PORTLAND_TEAM_ID
        ? selectedGame.awayTeam.teamId
        : selectedGame.homeTeam.teamId;

    setSaving(true);
    setSaveSuccess(false);
    const api = new ExplorerAPI(ExplorerEnvironment.NBA);
    api
      .saveRotationPlan(gameId, {
        opponentTeamId,
        slotAssignments,
        excludedPlayerIds: Array.from(excludedPlayerIds),
      })
      .then(() => {
        setHasUnsavedChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      })
      .catch((error) => {
        console.error("Failed to save rotation plan:", error);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return {
    slotAssignments,
    excludedPlayerIds,
    loadingPlan,
    saving,
    saveSuccess,
    hasUnsavedChanges,
    assignPlayer,
    clearSlot,
    carryOver,
    clearQuarter,
    excludePlayer,
    includePlayer,
    save,
  };
}
